/**
 * Dữ liệu mẫu và máy chủ giả của phân hệ Tenant.
 *
 * Đóng vai tenant-service khi chưa đăng nhập Keycloak thật hoặc khi URL có
 * `?mock=`. Máy chủ giả **từ chối y như backend**: cùng luật trạng thái
 * (`nghiep-vu/thao-tac.ts`, bản TypeScript của `Tenant.java`), cùng `error_code`
 * và cùng câu `detail` chép từ `loi.properties` của tenant-service. Mock dễ dãi
 * hơn backend thì form không bao giờ chạy nhánh lỗi, và lỗi đó chỉ lộ ra lúc
 * nối API thật.
 *
 * Mỗi nhánh đều có ít nhất một bản ghi, **kể cả nhánh xấu**: tenant trả về
 * DRAFT sau khi triển khai thất bại, tenant đang triển khai dở, tenant không có
 * domain hay người liên hệ nào, tenant đã xoá.
 */
import type { ApiError } from '../lib/errors';
import { mayChuGoiTaiNguyen } from './goi-tai-nguyen';
import { dongHanMucToken } from './han-muc-token';
import {
  canTroDuyet,
  choXacMinhDomain,
  domainChinhSauKhiGo,
  duyetDuoc,
  goDomainDuoc,
  khoiPhucDuoc,
  suaDuoc,
  suaSlugDuoc,
  tamNgungDuoc,
  kiemLyDo,
  trangThaiSauKhoiPhuc,
  xacMinhDomainDuoc,
} from '../modules/tenant/nghiep-vu/thao-tac';
import type { CanTroDuyet } from '../modules/tenant/nghiep-vu/thao-tac';
import type {
  PlanCode,
  ProvisioningStep,
  Tenant,
  TenantCreate,
  TenantDetail,
  TenantDomain,
  TenantProvisioning,
  TenantQuota,
  TenantUpdate,
} from '../modules/tenant/types';

const GiB = 1024 ** 3;

/** `sub` của một quản trị nền tảng khác người đang đăng nhập, cho domain xác minh thủ công mẫu. */
const NGUOI_DUYET_MAU = '5b0e8c1d-3a7f-4e2b-9d6c-1f0a2b3c4d5e';

/**
 * Dòng `plan_quota` mẫu mà gói hạn mức không mang (ADR-0105): `S3_BYTES` (Chương 4 có, chưa ai áp) để soát dòng ngoài
 * gói, và một tài nguyên giao diện chưa biết ở gói ENTERPRISE để soát nhãn chung của tài nguyên lạ.
 */
const HAN_MUC_NGOAI_GOI: Record<PlanCode, TenantQuota[]> = {
  TRIAL: [{ resource: 'S3_BYTES', limit_value: 5 * GiB, used: null }],
  STANDARD: [{ resource: 'S3_BYTES', limit_value: 50 * GiB, used: null }],
  PROFESSIONAL: [{ resource: 'S3_BYTES', limit_value: 200 * GiB, used: null }],
  ENTERPRISE: [{ resource: 'SMS_MESSAGES_MONTH', limit_value: 5_000, used: null }],
};

/** Phần chung của một tenant mới tạo, theo giá trị mặc định của Chương 4. */
const MAC_DINH: Omit<TenantDetail, 'tenant_id' | 'slug' | 'display_name' | 'created_at' | 'updated_at'> = {
  legal_name: null,
  tax_code: null,
  plan_code: 'TRIAL',
  resource_package_code: null,
  deployment_mode: 'SAAS',
  status: 'DRAFT',
  region: 'vn-hcm',
  tier: 'POOL',
  db_cluster_id: null,
  db_name: null,
  schema_version: '0.0.0',
  keycloak_group_id: null,
  keycloak_group_path: null,
  suspended_at: null,
  deleted_at: null,
  domains: [],
  admin_contacts: [],
  quota: [],
  resource_package: null,
  provisioning_steps: [],
};

/** Đã chạy xong bước provisioning: DB, migration, group Keycloak đều có. */
function daTrienKhai(slug: string, groupId: string) {
  return {
    db_cluster_id: 'pg-cluster-01',
    db_name: `tenant_${slug.replace(/-/g, '_')}`,
    schema_version: '1.0.0',
    keycloak_group_id: groupId,
    keycloak_group_path: `/tenants/${slug}`,
  };
}

function buoc(
  step: string,
  status: ProvisioningStep['status'],
  luc: string,
  them: Partial<ProvisioningStep> = {},
): ProvisioningStep {
  return { step, status, retry_count: 0, last_error: null, created_at: luc, updated_at: luc, ...them };
}

/**
 * Bước 3–12 của workflow onboarding, đúng tên và đúng thứ tự worker ghi vào
 * `provisioning_job` (`workers/provisioning-worker/internal/onboard/steps.go`).
 * Bước 1–2 do tenant-service làm trước khi khởi động workflow, nên không có ở đây.
 */
const CAC_BUOC = [
  'CREATE_DB',
  'MIGRATE',
  'KEYCLOAK_GROUP',
  'INGRESS',
  'INSTALL_MODULES',
  'SUBSCRIPTION',
  'SEED_DATA',
  'HEALTH_CHECK',
  'ACTIVATE',
] as const;

/** Chỉ số bước 5–6: gộp group Keycloak, chờ xác minh domain và Tenant Admin đầu tiên. */
const BUOC_KEYCLOAK = CAC_BUOC.indexOf('KEYCLOAK_GROUP');

/**
 * Ảnh chụp tiến độ: `soXong` bước đầu đã xong, bước kế mang `trangThaiDang`,
 * các bước sau còn chờ. Một hàm cho cả dữ liệu mẫu lẫn lượt duyệt mô phỏng, nên
 * hai chỗ không lệch nhau về tên và thứ tự bước.
 */
function tienDo(
  luc: string,
  soXong: number,
  trangThaiDang: ProvisioningStep['status'] = 'RUNNING',
  themVaoBuocDang: Partial<ProvisioningStep> = {},
): ProvisioningStep[] {
  return CAC_BUOC.map((ten, i) => {
    if (i < soXong) return buoc(ten, 'SUCCEEDED', luc);
    if (i === soXong) return buoc(ten, trangThaiDang, luc, themVaoBuocDang);
    return buoc(ten, 'PENDING', luc);
  });
}

const BUOC_XONG = (luc: string): ProvisioningStep[] => tienDo(luc, CAC_BUOC.length);

const DU_LIEU: TenantDetail[] = [
  {
    ...MAC_DINH,
    ...daTrienKhai('foxai', '7f1c2a4e-0d3b-4c8e-9a51-2b6f7d1e0a01'),
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000001',
    slug: 'foxai',
    display_name: 'FoxAI',
    legal_name: 'Công ty Cổ phần FOXAI Việt Nam',
    tax_code: '0316123456',
    plan_code: 'ENTERPRISE',
    status: 'ACTIVE',
    tier: 'DEDICATED',
    created_at: '2026-10-01T02:20:00Z',
    updated_at: '2026-10-12T08:41:00Z',
    domains: [
      {
        domain: 'foxai.com.vn',
        is_primary: true,
        verified_at: '2026-10-01T03:00:00Z',
        verify_method: 'ADMIN_APPROVAL',
        verified_by: NGUOI_DUYET_MAU,
        verify_note: 'Xác nhận qua hợp đồng số 01/2026/HĐ-FOXAI',
        verification: null,
        created_at: '2026-10-01T02:20:00Z',
      },
      {
        domain: 'foxai.vn',
        is_primary: false,
        verified_at: null,
        verify_method: null,
        verified_by: null,
        verify_note: null,
        verification: null,
        created_at: '2026-10-12T08:41:00Z',
      },
    ],
    admin_contacts: [
      {
        id: 'c0000001-0000-4000-8000-000000000001',
        full_name: 'Nguyễn Văn An',
        email: 'an.nguyen@foxai.com.vn',
        phone: '0901234567',
        is_primary: true,
        created_at: '2026-10-01T02:20:00Z',
      },
      {
        id: 'c0000001-0000-4000-8000-000000000002',
        full_name: 'Trần Thị Bình',
        email: 'binh.tran@foxai.com.vn',
        phone: null,
        is_primary: false,
        created_at: '2026-10-01T02:20:00Z',
      },
    ],
    provisioning_steps: BUOC_XONG('2026-10-01T03:05:00Z'),
  },
  {
    ...MAC_DINH,
    ...daTrienKhai('minh-long', '7f1c2a4e-0d3b-4c8e-9a51-2b6f7d1e0a02'),
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000002',
    slug: 'minh-long',
    display_name: 'Minh Long',
    legal_name: 'Công ty TNHH Thương mại Minh Long',
    tax_code: '0312987654',
    status: 'TRIAL',
    created_at: '2026-10-08T07:02:00Z',
    updated_at: '2026-10-08T07:16:00Z',
    domains: [
      {
        domain: 'minhlong.vn',
        is_primary: true,
        verified_at: '2026-10-08T07:10:00Z',
        verify_method: 'DNS_TXT',
        verified_by: null,
        verify_note: null,
        verification: null,
        created_at: '2026-10-08T07:02:00Z',
      },
    ],
    admin_contacts: [
      {
        id: 'c0000002-0000-4000-8000-000000000001',
        full_name: 'Lê Minh Cường',
        email: 'cuong.le@minhlong.vn',
        phone: '0987654321',
        is_primary: true,
        created_at: '2026-10-08T07:02:00Z',
      },
    ],
    provisioning_steps: BUOC_XONG('2026-10-08T07:15:00Z'),
  },
  {
    // Đang chạy workflow onboarding dở: màn chi tiết tự hỏi lại mỗi 5 giây.
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000003',
    slug: 'dai-phat',
    display_name: 'Đại Phát Miền Nam',
    legal_name: 'Công ty Cổ phần Đầu tư và Phát triển Đại Phát Miền Nam',
    plan_code: 'STANDARD',
    status: 'PROVISIONING',
    created_at: '2026-10-19T01:30:00Z',
    updated_at: '2026-10-19T01:33:00Z',
    domains: [
      {
        domain: 'daiphat.com.vn',
        is_primary: true,
        verified_at: null,
        verify_method: null,
        verified_by: null,
        verify_note: null,
        verification: null,
        created_at: '2026-10-19T01:30:00Z',
      },
    ],
    admin_contacts: [
      {
        id: 'c0000003-0000-4000-8000-000000000001',
        full_name: 'Phạm Quốc Đạt',
        email: 'dat.pham@daiphat.com.vn',
        phone: '0912345678',
        is_primary: true,
        created_at: '2026-10-19T01:30:00Z',
      },
    ],
    provisioning_steps: tienDo('2026-10-19T01:33:00Z', 1, 'RUNNING', { retry_count: 1 }),
  },
  {
    // Nhánh xấu: bước 3 lỗi sau 3 lần thử, workflow trả tenant về DRAFT (UC-02).
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000004',
    slug: 'an-khang',
    display_name: 'An Khang',
    legal_name: 'Công ty TNHH An Khang',
    plan_code: 'STANDARD',
    status: 'DRAFT',
    created_at: '2026-10-14T09:45:00Z',
    updated_at: '2026-10-14T09:52:00Z',
    domains: [
      {
        domain: 'ankhang.vn',
        is_primary: true,
        verified_at: null,
        verify_method: null,
        verified_by: null,
        verify_note: null,
        verification: null,
        created_at: '2026-10-14T09:45:00Z',
      },
    ],
    admin_contacts: [
      {
        id: 'c0000004-0000-4000-8000-000000000001',
        full_name: 'Võ Thị Hạnh',
        email: 'hanh.vo@ankhang.vn',
        phone: null,
        is_primary: true,
        created_at: '2026-10-14T09:45:00Z',
      },
    ],
    provisioning_steps: tienDo('2026-10-14T09:52:00Z', 0, 'FAILED', {
      retry_count: 3,
      last_error: 'Không kết nối được cụm pg-cluster-02: timeout sau 30 giây.',
    }),
  },
  {
    // On-premise chỉ quản lý hồ sơ và license (Q-15): không domain, không hạ tầng.
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000005',
    slug: 'hoa-binh',
    display_name: 'Tập đoàn Hoà Bình',
    legal_name: 'Công ty Cổ phần Tập đoàn Xây dựng Hoà Bình',
    tax_code: '0302158498',
    plan_code: 'ENTERPRISE',
    deployment_mode: 'ON_PREMISE',
    status: 'ACTIVE',
    created_at: '2026-09-20T03:00:00Z',
    updated_at: '2026-09-20T03:10:00Z',
    admin_contacts: [
      {
        id: 'c0000005-0000-4000-8000-000000000001',
        full_name: 'Đặng Thu Trang',
        email: 'trang.dang@hbcg.vn',
        phone: '0283932 0000',
        is_primary: true,
        created_at: '2026-09-20T03:00:00Z',
      },
    ],
  },
  {
    // Hồ sơ nháp on-premise: duyệt là sang thẳng ACTIVE, không có bước nào (mục 14.1).
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000011',
    slug: 'phuong-nam',
    display_name: 'Phương Nam',
    legal_name: 'Công ty TNHH Sản xuất Thương mại Phương Nam',
    plan_code: 'ENTERPRISE',
    deployment_mode: 'ON_PREMISE',
    created_at: '2026-10-20T04:15:00Z',
    updated_at: '2026-10-20T04:15:00Z',
    admin_contacts: [
      {
        id: 'c0000011-0000-4000-8000-000000000001',
        full_name: 'Huỳnh Gia Bảo',
        email: 'bao.huynh@phuongnam.com.vn',
        phone: '0909112233',
        is_primary: true,
        created_at: '2026-10-20T04:15:00Z',
      },
    ],
  },
  {
    ...MAC_DINH,
    ...daTrienKhai('tan-tien', '7f1c2a4e-0d3b-4c8e-9a51-2b6f7d1e0a06'),
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000006',
    slug: 'tan-tien',
    display_name: 'Tân Tiến',
    status: 'SUSPENDED',
    suspended_at: '2026-10-13T15:10:00Z',
    created_at: '2026-09-30T04:15:00Z',
    updated_at: '2026-10-13T15:10:00Z',
    domains: [
      {
        domain: 'tantien.com',
        is_primary: true,
        verified_at: '2026-09-30T05:00:00Z',
        verify_method: 'DNS_TXT',
        verified_by: null,
        verify_note: null,
        verification: null,
        created_at: '2026-09-30T04:15:00Z',
      },
    ],
    provisioning_steps: BUOC_XONG('2026-09-30T04:30:00Z'),
  },
  {
    ...MAC_DINH,
    ...daTrienKhai('phuong-nam', '7f1c2a4e-0d3b-4c8e-9a51-2b6f7d1e0a07'),
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000007',
    slug: 'phuong-nam',
    display_name: 'Phương Nam',
    legal_name: 'Công ty Cổ phần Văn hoá Phương Nam',
    plan_code: 'PROFESSIONAL',
    // Gói tài nguyên tự tạo (ADR-0091): 80 triệu token mỗi tháng trên gói nền Chuyên nghiệp.
    resource_package_code: 'CHUYEN_NGHIEP_MO_RONG',
    status: 'GRACE_PERIOD',
    created_at: '2026-09-10T02:00:00Z',
    updated_at: '2026-10-15T00:00:00Z',
    domains: [
      {
        domain: 'phuongnam.vn',
        is_primary: true,
        verified_at: '2026-09-10T02:30:00Z',
        verify_method: 'DNS_TXT',
        verified_by: null,
        verify_note: null,
        verification: null,
        created_at: '2026-09-10T02:00:00Z',
      },
    ],
    provisioning_steps: BUOC_XONG('2026-09-10T02:20:00Z'),
  },
  {
    // On-premise thứ hai: license đang trong ân hạn, có yêu cầu gia hạn chờ duyệt
    // (`mock/license.ts`). Dùng để soát tab License của màn chi tiết.
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000011',
    slug: 'ngan-hang-phuong-nam',
    display_name: 'Ngân hàng Phương Nam',
    legal_name: 'Ngân hàng Thương mại Cổ phần Phương Nam',
    tax_code: '0300852005',
    plan_code: 'ENTERPRISE',
    deployment_mode: 'ON_PREMISE',
    status: 'ACTIVE',
    created_at: '2025-09-05T02:00:00Z',
    updated_at: '2025-09-05T02:10:00Z',
    admin_contacts: [
      {
        id: 'c0000011-0000-4000-8000-000000000001',
        full_name: 'Huỳnh Gia Bảo',
        email: 'bao.huynh@phuongnambank.vn',
        phone: null,
        is_primary: true,
        created_at: '2025-09-05T02:00:00Z',
      },
    ],
  },
  {
    // Hồ sơ nháp mới tạo, chưa khai domain hay người liên hệ nào.
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000008',
    slug: 'sao-mai',
    display_name: 'Sao Mai',
    created_at: '2026-10-19T03:05:00Z',
    updated_at: '2026-10-19T03:05:00Z',
  },
  {
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000009',
    slug: 'viet-thanh',
    display_name: 'Việt Thành',
    plan_code: 'STANDARD',
    status: 'ARCHIVED',
    created_at: '2026-08-02T01:00:00Z',
    updated_at: '2026-10-01T02:00:00Z',
  },
  {
    ...MAC_DINH,
    tenant_id: '0a7d3c5e-1f2b-4e6a-9c8d-000000000010',
    slug: 'cuu-long',
    display_name: 'Cửu Long',
    status: 'DELETED',
    deleted_at: '2026-09-01T02:00:00Z',
    created_at: '2026-07-15T01:00:00Z',
    updated_at: '2026-09-01T02:00:00Z',
  },
];

/** Domain dành riêng cho nhân sự nền tảng, giá trị tạm của ADR-0007 mục 8. */
const DOMAIN_DANH_RIENG = ['ops.platform.test'];

/** Mặc định `platform.tenant.public-email-domains` của tenant-service (ADR-0027 mục 4, GG-FX-39). */
const DOMAIN_CONG_CONG = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'live.com', 'icloud.com', 'me.com',
  'msn.com', 'aol.com', 'proton.me', 'protonmail.com', 'yandex.com', 'mail.ru', 'zoho.com', 'gmx.com',
];

/** `400 INVALID_ARGUMENT` kèm `errors[].pointer` như `TenantService#rejectReservedOrPublic`. */
const loiCongCong = (domain: string, pointer: string) => {
  const cau = `Domain ${domain} là domain email công cộng, không gán cho tenant được. Tổ chức không có domain riêng thì chọn "Tổ chức dùng email cá nhân" thay vì thêm domain này.`;
  return loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [{ pointer, detail: cau }]);
};

/** Bản sao trong phiên: thao tác ghi thấy được ngay, như API thật. */
let cacTenant: TenantDetail[] = structuredClone(DU_LIEU);

const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

const khongTimThay = () =>
  loi(
    'NOT_FOUND',
    404,
    'Không tìm thấy tenant với mã này. Kiểm tra lại đường dẫn hoặc tải lại danh sách tenant.',
  );

function tim(id: string): TenantDetail {
  const tenant = cacTenant.find((t) => t.tenant_id === id);
  if (!tenant) throw khongTimThay();
  return tenant;
}

/** Bỏ các trường chỉ có ở chi tiết, như một dòng của `GET /api/v1/tenants`. */
function dong({
  domains: _d,
  admin_contacts: _a,
  quota: _q,
  resource_package: _r,
  provisioning_steps: _p,
  ...tenant
}: TenantDetail): Tenant {
  return tenant;
}

/**
 * Giá trị TXT giả, ổn định theo `(tenant_id, domain)` như HMAC thật của
 * tenant-service (ADR-0030): cùng domain luôn ra cùng một chuỗi Base64URL.
 */
function maXacMinhGia(tenantId: string, domain: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (const ky of `${tenantId}:${domain}`) {
    h1 = Math.imul(h1 ^ ky.charCodeAt(0), 0x01000193) >>> 0;
    h2 = Math.imul(h2 + ky.charCodeAt(0), 0x5bd1e995) >>> 0;
  }
  const bang = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  let ra = '';
  for (let i = 0; i < 43; i++) {
    const tron = (Math.imul(h1 ^ (h2 >>> (i % 17)), i + 1) ^ Math.imul(h2, i + 7)) >>> 0;
    ra += bang[tron % 64];
  }
  return ra;
}

/**
 * Hạn mức đọc theo gói **hiện tại**, như backend tra `plan_quota` mỗi lần đọc;
 * bản ghi DNS cần tạo tính lại mỗi lần đọc cho domain chưa xác minh (ADR-0054).
 */
const kemHanMuc = (tenant: TenantDetail): TenantDetail => ({
  ...structuredClone(tenant),
  domains: tenant.domains.map((d) => ({
    ...d,
    verification: d.verified_at
      ? null
      : {
          record_name: `_platform-verify.${d.domain}`,
          record_type: 'TXT' as const,
          record_value: maXacMinhGia(tenant.tenant_id, d.domain),
        },
  })),
  // Như tenant-service: plan_quota của gói nền, số của gói hạn mức thắng, sắp theo tên tài nguyên
  quota: [
    ...structuredClone(HAN_MUC_NGOAI_GOI[tenant.plan_code]),
    ...mayChuGoiTaiNguyen.dongHanMuc(tenant.resource_package_code, tenant.plan_code),
    ...dongHanMucToken(tenant.tenant_id, tenant.plan_code, tenant.resource_package_code),
  ].sort((a, b) => a.resource.localeCompare(b.resource)),
  resource_package: mayChuGoiTaiNguyen.cuaTenant(tenant.resource_package_code, tenant.plan_code),
});

/**
 * Gói tài nguyên gửi lên phải đang bật; `plan_code` của tenant lấy theo gói nền của gói (ADR-0091 mục 1). Gửi kèm
 * `plan_code` khác gói nền là 400, như tenant-service.
 */
function goiTaiNguyenHopLe(ma: string, plan: PlanCode | undefined): { ma: string; plan: PlanCode } {
  const g = mayChuGoiTaiNguyen.timDangBat(ma);
  if (!g) {
    throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
      { pointer: '#/resource_package_code', detail: `Gói tài nguyên ${ma} không có hoặc đã ngừng. Chọn gói khác.` },
    ]);
  }
  if (plan && plan !== g.plan_code) {
    throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
      { pointer: '#/plan_code', detail: `Gói ${g.name} thuộc gói nền ${g.plan_code}, không phải ${plan}.` },
    ]);
  }
  return { ma: g.code, plan: g.plan_code };
}

function ghi(id: string, doi: (tenant: TenantDetail) => TenantDetail): TenantDetail {
  const moi = { ...doi(tim(id)), updated_at: new Date().toISOString() };
  cacTenant = cacTenant.map((t) => (t.tenant_id === id ? moi : t));
  return kemHanMuc(moi);
}

const slugDaDung = (slug: string, tru?: string) =>
  cacTenant.some((t) => t.slug === slug && t.tenant_id !== tru);

const slugTrung = (slug: string) =>
  loi(
    'TENANT_DOMAIN_CONFLICT',
    409,
    `Slug ${slug} đã được dùng cho một tenant khác, kể cả tenant đã xoá. Chọn slug khác.`,
  );

/** Câu từ chối duyệt, chép từ `loi.properties` của tenant-service (ADR-0028 mục 4). */
const CAU_CAN_TRO: Record<CanTroDuyet, (tenant: TenantDetail) => string> = {
  THIEU_DOMAIN_CHINH: () =>
    'Hồ sơ tenant chưa có domain email, mà onboarding cần domain chính để xác minh và để Tenant Admin đăng nhập. Bổ sung domain cho hồ sơ rồi duyệt lại.',
  THIEU_LIEN_HE_CHINH: () =>
    'Hồ sơ tenant chưa có người liên hệ quản trị, mà onboarding tạo Tenant Admin đầu tiên từ người liên hệ chính. Bổ sung người liên hệ rồi duyệt lại.',
  EMAIL_NGOAI_DOMAIN_CHINH: (tenant) => {
    const email = tenant.admin_contacts.find((c) => c.is_primary)?.email ?? '';
    const domain = tenant.domains.find((d) => d.is_primary)?.domain ?? '';
    return `Email ${email} của người liên hệ chính không thuộc domain chính ${domain}, nên Tenant Admin đầu tiên không đăng nhập được sau onboarding. Đổi người liên hệ chính sang một email thuộc ${domain} rồi duyệt lại.`;
  },
};

/** Nhịp một bước onboarding ở máy chủ giả. Worker thật xong cả 9 bước trong vài giây. */
const NHIP_BUOC_MS = 1_500;

/** Tenant đang chạy onboarding mô phỏng trong phiên này: mã tenant → lúc bấm duyệt. */
const lucDuyet = new Map<string, number>();

/**
 * Đẩy tiến độ của tenant vừa duyệt theo thời gian trôi, như worker ghi dần từng
 * dòng `provisioning_job`, rồi kết thúc ở bước 12: PROVISIONING → TRIAL.
 *
 * Domain chính chưa xác minh thì dừng ở bước 5–6 và **đứng yên ở đó** (bước 5c
 * chờ signal `tenant.domain.verified`, tối đa 14 ngày — ADR-0009). Cho chạy tiếp
 * thì mock dễ hơn backend, và người dựng giao diện sẽ không bao giờ thấy cảnh
 * tiến độ nằm im mà vẫn là bình thường.
 */
function tienTrienOnboarding(tenant: TenantDetail): void {
  const batDau = lucDuyet.get(tenant.tenant_id);
  if (batDau === undefined || tenant.status !== 'PROVISIONING') return;

  const tran = choXacMinhDomain(tenant) ? BUOC_KEYCLOAK : CAC_BUOC.length;
  const soXong = Math.min(tran, Math.floor((Date.now() - batDau) / NHIP_BUOC_MS));
  const dangCo = tenant.provisioning_steps.filter((b) => b.status === 'SUCCEEDED').length;
  if (soXong === dangCo) return;

  const luc = new Date().toISOString();
  if (soXong < CAC_BUOC.length) {
    ghi(tenant.tenant_id, (t) => ({ ...t, provisioning_steps: tienDo(luc, soXong) }));
    return;
  }
  lucDuyet.delete(tenant.tenant_id);
  ghi(tenant.tenant_id, (t) => ({
    ...t,
    ...daTrienKhai(t.slug, crypto.randomUUID()),
    status: 'TRIAL',
    provisioning_steps: BUOC_XONG(luc),
  }));
}

/** Số lần đã kiểm DNS từng domain trong phiên: lần đầu chưa khớp, lần sau khớp (để thấy cả hai nhánh). */
const soLanKiemDns = new Map<string, number>();

/**
 * Domain của tenant cho hai endpoint xác minh (ADR-0054): tenant phải còn
 * dùng được (không ARCHIVED, DELETED) và domain phải thuộc tenant đó.
 */
function domainDeXacMinh(id: string, domain: string): { tenant: TenantDetail; dong: TenantDomain } {
  const tenant = tim(id);
  if (!xacMinhDomainDuoc(tenant.status)) {
    throw loi(
      'TENANT_STATUS_CONFLICT',
      409,
      `Tenant đang ở trạng thái ${tenant.status} nên không xác minh domain được. Tenant đã lưu trữ hoặc đã xoá không nhận domain mới.`,
    );
  }
  const dong = tenant.domains.find((d) => d.domain === domain.toLowerCase());
  if (!dong) {
    throw loi(
      'NOT_FOUND',
      404,
      `Domain ${domain.toLowerCase()} không thuộc tenant này. Tải lại tenant để xem danh sách domain mới nhất.`,
    );
  }
  return { tenant, dong };
}

/** Ghi domain đã xác minh vào tenant, trả đúng dòng domain như backend (kèm `verification` tính lại). */
function ghiXacMinh(id: string, domain: string, doi: Partial<TenantDomain>): TenantDomain {
  const moi = ghi(id, (t) => ({
    ...t,
    domains: t.domains.map((d) => (d.domain === domain ? { ...d, ...doi } : d)),
  }));
  return moi.domains.find((d) => d.domain === domain) as TenantDomain;
}

const dongDomain = (id: string, domain: string): TenantDomain =>
  kemHanMuc(tim(id)).domains.find((d) => d.domain === domain) as TenantDomain;

/**
 * Máy chủ giả: từng hàm ứng với một endpoint nhóm Tenant (mục 6.2). Hàm **ném**
 * `ApiError` như backend trả Problem Details; repository bọc qua `mockRequest`
 * để có độ trễ và các trạng thái `?mock=`.
 */
export const mayChuTenant = {
  /**
   * Gói tự tạo đổi gói nền: mọi tenant của gói đổi `plan_code` theo (ADR-0091 mục 1), như tenant-service làm
   * trong cùng transaction với `PATCH /api/v1/security/resource-packages/{code}`.
   */
  keoGoiNen: (ma: string, plan: PlanCode): void => {
    cacTenant = cacTenant.map((t) => (t.resource_package_code === ma ? { ...t, plan_code: plan } : t));
  },

  /** `GET /api/v1/tenants`: tenant tạo sau đứng trước, gồm cả tenant đã xoá. */
  danhSach: (): Tenant[] => {
    // Trạng thái của tenant đang onboarding phải đúng ở cả danh sách, không chỉ
    // ở màn chi tiết đang mở.
    for (const id of cacTenant.map((t) => t.tenant_id)) tienTrienOnboarding(tim(id));
    return [...cacTenant].sort((a, b) => b.created_at.localeCompare(a.created_at)).map(dong);
  },

  chiTiet: (id: string): TenantDetail => {
    tienTrienOnboarding(tim(id));
    return kemHanMuc(tim(id));
  },

  tao: (du: TenantCreate): TenantDetail => {
    if (slugDaDung(du.slug)) throw slugTrung(du.slug);
    if (du.deployment_mode === 'ON_PREMISE' && (du.domains?.length ?? 0) > 0) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        { pointer: '#/domains', detail: 'Tenant on-premise không có domain email. Để trống danh sách domain.' },
      ]);
    }
    const domains = (du.domains ?? []).map((d) => d.toLowerCase());
    for (const [i, domain] of domains.entries()) {
      if (DOMAIN_CONG_CONG.includes(domain)) throw loiCongCong(domain, `#/domains/${i}`);
      if (DOMAIN_DANH_RIENG.some((r) => domain === r || domain.endsWith(`.${r}`))) {
        throw loi(
          'TENANT_DOMAIN_CONFLICT',
          409,
          `Domain ${domain} dành cho nhân sự quản trị nền tảng, không gán được cho tenant. Bỏ domain này khỏi danh sách.`,
        );
      }
      if (cacTenant.some((t) => t.domains.some((d) => d.domain === domain))) {
        throw loi(
          'TENANT_DOMAIN_CONFLICT',
          409,
          `Domain ${domain} đã được đăng ký cho một tenant khác. Bỏ domain này khỏi danh sách.`,
        );
      }
    }

    const goiTN = du.resource_package_code ? goiTaiNguyenHopLe(du.resource_package_code, du.plan_code) : null;
    const luc = new Date().toISOString();
    const tenant: TenantDetail = {
      ...MAC_DINH,
      tenant_id: crypto.randomUUID(),
      slug: du.slug,
      display_name: du.display_name,
      legal_name: du.legal_name ?? null,
      tax_code: du.tax_code ?? null,
      plan_code: goiTN?.plan ?? du.plan_code ?? 'TRIAL',
      resource_package_code: goiTN?.ma ?? du.plan_code ?? 'TRIAL',
      deployment_mode: du.deployment_mode ?? 'SAAS',
      tier: du.tier ?? 'POOL',
      created_at: luc,
      updated_at: luc,
      domains: domains.map((domain, i) => ({
        domain,
        is_primary: i === 0,
        verified_at: null,
        verify_method: null,
        verified_by: null,
        verify_note: null,
        verification: null,
        created_at: luc,
      })),
      admin_contacts: (du.admin_contacts ?? []).map((c, i) => ({
        id: crypto.randomUUID(),
        full_name: c.full_name,
        email: c.email,
        // ADR-0101: trùng email đăng nhập thì như backend lưu null
        email_nhan_thu:
          c.email_nhan_thu && c.email_nhan_thu.trim().toLowerCase() !== c.email.trim().toLowerCase()
            ? c.email_nhan_thu.trim().toLowerCase()
            : null,
        phone: c.phone ?? null,
        is_primary: i === 0,
        created_at: luc,
      })),
    };
    // ADR-0099: chỉ ghi khi khác mặc định (true), như EmailDomainPolicy.datKhiTao
    if (du.chi_email_domain_xac_minh === false) tenant.chi_email_domain_xac_minh = false;
    cacTenant = [...cacTenant, tenant];
    return kemHanMuc(tenant);
  },

  /** Domain đã xác minh của tenant (ADR-0099), cho `verified_domains` của chính sách tầng tenant. */
  domainDaXacMinh: (id: string): string[] =>
    cacTenant.find((t) => t.tenant_id === id)?.domains.filter((d) => d.verified_at).map((d) => d.domain) ?? [],

  /** Giá trị đầu của `auth.chi_email_domain_xac_minh` đặt lúc tạo; `undefined` là mặc định. */
  chiEmailDomainLucTao: (id: string): boolean | undefined =>
    cacTenant.find((t) => t.tenant_id === id)?.chi_email_domain_xac_minh,

  sua: (id: string, du: TenantUpdate): TenantDetail => {
    const hienTai = tim(id);
    if (!suaDuoc(hienTai.status)) {
      throw loi(
        'TENANT_STATUS_CONFLICT',
        409,
        `Tenant đang ở trạng thái ${hienTai.status} nên không sửa được. Tenant ở PROVISIONING, ARCHIVED hoặc DELETED không sửa được hồ sơ, gói và tầng.`,
      );
    }
    // Gửi lại đúng slug hiện tại không tính là sửa slug (ADR-0025 mục 1).
    if (du.slug !== undefined && du.slug !== hienTai.slug) {
      if (!suaSlugDuoc(hienTai.status)) {
        throw loi(
          'TENANT_STATUS_CONFLICT',
          409,
          `Slug đã khoá từ khi tenant được duyệt; tenant đang ở trạng thái ${hienTai.status}. Giữ nguyên slug hiện tại.`,
        );
      }
      if (slugDaDung(du.slug, id)) throw slugTrung(du.slug);
    }
    // Đổi gói tài nguyên: plan_code theo gói nền. Chỉ đổi plan_code: về gói mặc định của plan_code mới.
    const goiTN = du.resource_package_code ? goiTaiNguyenHopLe(du.resource_package_code, du.plan_code) : null;
    return ghi(id, (t) => ({
      ...t,
      ...(goiTN
        ? { plan_code: goiTN.plan, resource_package_code: goiTN.ma }
        : du.plan_code && du.plan_code !== t.plan_code
          ? { plan_code: du.plan_code, resource_package_code: du.plan_code }
          : {}),
      slug: du.slug ?? t.slug,
      display_name: du.display_name ?? t.display_name,
      legal_name: du.legal_name ?? t.legal_name,
      tax_code: du.tax_code ?? t.tax_code,
      tier: du.tier ?? t.tier,
    }));
  },

  /**
   * `POST /api/v1/tenants/{id}/provision`: duyệt hồ sơ DRAFT (UC-02).
   *
   * SAAS sang PROVISIONING kèm `job_id`, rồi tiến độ chạy dần theo
   * `tienTrienOnboarding`; ON_PREMISE sang thẳng ACTIVE, `job_id` null và không
   * có bước nào (ADR-0026 mục 2, mục 14.1).
   */
  duyet: (id: string): TenantProvisioning => {
    const hienTai = tim(id);
    if (!duyetDuoc(hienTai.status)) {
      throw loi(
        'TENANT_STATUS_CONFLICT',
        409,
        `Tenant đang ở trạng thái ${hienTai.status} nên không duyệt được. Chỉ duyệt được hồ sơ ở DRAFT; tải lại tenant để xem trạng thái mới.`,
      );
    }
    // Backend ném lý do đầu tiên gặp phải, mock cũng vậy.
    const canTro = canTroDuyet(hienTai);
    if (canTro.length > 0) {
      throw loi('TENANT_PROFILE_INCOMPLETE', 422, CAU_CAN_TRO[canTro[0]](hienTai));
    }

    if (hienTai.deployment_mode === 'ON_PREMISE') {
      return { job_id: null, tenant: ghi(id, (t) => ({ ...t, status: 'ACTIVE' })) };
    }
    lucDuyet.set(id, Date.now());
    const luc = new Date().toISOString();
    return {
      job_id: `onboard-${id}`,
      tenant: ghi(id, (t) => ({
        ...t,
        status: 'PROVISIONING',
        provisioning_steps: tienDo(luc, 0),
      })),
    };
  },

  tamNgung: (id: string): TenantDetail => {
    const { status } = tim(id);
    if (!tamNgungDuoc(status)) {
      throw loi(
        'TENANT_STATUS_CONFLICT',
        409,
        `Tenant đang ở trạng thái ${status} nên không tạm ngưng được. Chỉ tạm ngưng được tenant ở TRIAL, ACTIVE hoặc GRACE_PERIOD.`,
      );
    }
    return ghi(id, (t) => ({ ...t, status: 'SUSPENDED', suspended_at: new Date().toISOString() }));
  },

  /** Trước GĐ4 chưa có `tenant_subscription` nào, nên tenant SAAS luôn về TRIAL. */
  khoiPhuc: (id: string): TenantDetail => {
    const { status } = tim(id);
    if (!khoiPhucDuoc(status)) {
      throw loi(
        'TENANT_STATUS_CONFLICT',
        409,
        `Tenant đang ở trạng thái ${status}, không bị tạm ngưng, nên không khôi phục được. Tải lại tenant để xem trạng thái mới.`,
      );
    }
    return ghi(id, (t) => ({
      ...t,
      status: trangThaiSauKhoiPhuc(t.deployment_mode, false),
      suspended_at: null,
    }));
  },

  /**
   * `POST /api/v1/tenants/{id}/domains/{domain}/check` (ADR-0054 mục 2). Lần
   * kiểm đầu của mỗi domain trong phiên coi là DNS chưa khớp, lần sau khớp —
   * để giao diện đi được cả hai nhánh. Đã xác minh thì trả nguyên trạng.
   */
  /**
   * `POST /api/v1/tenants/{id}/domains`: thêm domain chưa xác minh, không phải
   * domain chính. Từ chối như backend: on-premise, domain dành riêng, domain đã
   * thuộc tenant nào đó (kể cả chính tenant này).
   */
  themDomain: (id: string, domainVao: string): TenantDomain => {
    const tenant = tim(id);
    const domain = domainVao.toLowerCase();
    if (tenant.deployment_mode === 'ON_PREMISE') {
      throw loi('INVALID_ARGUMENT', 400, 'Tenant on-premise không có domain email.');
    }
    if (DOMAIN_CONG_CONG.includes(domain)) throw loiCongCong(domain, '#/domain');
    if (DOMAIN_DANH_RIENG.some((r) => domain === r || domain.endsWith(`.${r}`))) {
      throw loi(
        'TENANT_DOMAIN_CONFLICT',
        409,
        `Domain ${domain} dành cho nhân sự quản trị nền tảng, không gán được cho tenant. Dùng domain khác.`,
      );
    }
    if (cacTenant.some((t) => t.domains.some((d) => d.domain === domain))) {
      throw loi('TENANT_DOMAIN_CONFLICT', 409, `Domain ${domain} đã được đăng ký. Dùng domain khác.`);
    }
    ghi(id, (t) => ({
      ...t,
      domains: [
        ...t.domains,
        {
          domain,
          // Như TenantService.addDomain: tenant chưa có domain chính thì domain thêm vào là chính
          is_primary: !t.domains.some((d) => d.is_primary),
          verified_at: null,
          verify_method: null,
          verified_by: null,
          verify_note: null,
          verification: null,
          created_at: new Date().toISOString(),
        },
      ],
    }));
    return dongDomain(id, domain);
  },

  /**
   * `DELETE /api/v1/tenants/{id}/domains/{domain}` (GG-FX-39): cùng luật với `DomainRemovalService`; luật "còn người
   * dùng trên domain" mock không biết người dùng nên không giả lập.
   */
  goDomain: (id: string, domainVao: string): void => {
    const tenant = tim(id);
    const domain = domainVao.toLowerCase();
    if (!tenant.domains.some((d) => d.domain === domain)) {
      throw loi('NOT_FOUND', 404, `Domain ${domain} không thuộc tenant này. Tải lại chi tiết tenant để xem danh sách domain hiện tại.`);
    }
    if (!goDomainDuoc(tenant, domain)) {
      throw loi(
        'TENANT_STATUS_CONFLICT',
        409,
        `Domain ${domain} là domain đã xác minh cuối cùng của tenant, gỡ đi thì không ai đăng nhập được. Xác minh domain khác trước, hoặc tắt "Chỉ email thuộc domain đã xác minh" ở tab Bảo mật rồi gỡ.`,
      );
    }
    const moi = domainChinhSauKhiGo(tenant, domain);
    ghi(id, (t) => ({
      ...t,
      domains: t.domains
        .filter((d) => d.domain !== domain)
        .map((d) => (d.domain === moi ? { ...d, is_primary: true } : d)),
    }));
  },

  kiemTraDomain: (id: string, domain: string): TenantDomain => {
    const { dong } = domainDeXacMinh(id, domain);
    if (dong.verified_at) return dongDomain(id, dong.domain);
    const lan = (soLanKiemDns.get(dong.domain) ?? 0) + 1;
    soLanKiemDns.set(dong.domain, lan);
    if (lan < 2) return dongDomain(id, dong.domain);
    return ghiXacMinh(id, dong.domain, {
      verified_at: new Date().toISOString(),
      verify_method: 'DNS_TXT',
      verified_by: null,
      verify_note: null,
    });
  },

  /**
   * `POST /api/v1/tenants/{id}/domains/{domain}/approve` (ADR-0054 mục 3).
   * Đã xác minh thì luỹ đẳng: trả nguyên trạng, không ghi đè cách xác minh.
   */
  xacMinhThuCong: (id: string, domain: string, lyDo: string, nguoiDuyet: string | undefined): TenantDomain => {
    const loiLyDo = kiemLyDo(lyDo);
    if (loiLyDo) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        {
          pointer: '#/reason',
          detail:
            loiLyDo === 'TRONG'
              ? 'Lý do không được để trống. Ghi căn cứ xác nhận khách sở hữu domain, ví dụ số hợp đồng.'
              : 'Lý do dài tối đa 1000 ký tự.',
        },
      ]);
    }
    const { dong } = domainDeXacMinh(id, domain);
    if (DOMAIN_DANH_RIENG.some((r) => dong.domain === r || dong.domain.endsWith(`.${r}`))) {
      throw loi(
        'TENANT_DOMAIN_CONFLICT',
        409,
        `Domain ${dong.domain} dành cho nhân sự quản trị nền tảng, không gán được cho tenant. Bỏ domain này khỏi danh sách.`,
      );
    }
    if (dong.verified_at) return dongDomain(id, dong.domain);
    return ghiXacMinh(id, dong.domain, {
      verified_at: new Date().toISOString(),
      verify_method: 'ADMIN_APPROVAL',
      verified_by: nguoiDuyet ?? null,
      verify_note: lyDo.trim(),
    });
  },
};
